import { useState } from 'react';
import { MoreHorizontal, Plus, Send } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Avatar } from '../../components/Avatar';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { OptionCard } from '../../components/OptionCard';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useActiveBusiness, useTeam } from '../../api/hooks';
import { apiPost } from '../../api/client';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate } from '../../lib/format';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner', admin: 'Admin', manager: 'Manager', staff: 'Staff', cashier: 'Cashier', readonly: 'Read-only',
};

/**
 * Team members — §5.15
 * Owner manages who can use this business's POS. Scoped to the active business
 * (member RLS): the API returns only this business's members.
 */
export function Team() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data: business } = useActiveBusiness();
  const { data: team = [] } = useTeam(business?.id ?? '');

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-5xl mx-auto">
      <PageHeader
        eyebrow={business?.name ?? 'Team'}
        title="Team"
        subtitle="Anyone who needs the POS for this business."
        action={
          <Button
            iconLeft={<Plus className="w-4 h-4" />}
            onClick={() => setInviteOpen(true)}
          >
            Invite team
          </Button>
        }
      />

      {team.length === 0 ? (
        <Card>
          <EmptyState
            title="It's just you here"
            description="Invite your cashier or manager to start using the POS together."
            action={
              <Button
                iconLeft={<Plus className="w-4 h-4" />}
                onClick={() => setInviteOpen(true)}
              >
                Invite team
              </Button>
            }
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto nice-scroll">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50 border-b border-zinc-200 dark:border-zinc-700">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-3">Member</th>
                  <th className="text-left font-medium px-4 py-3">Role</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">
                    Joined
                  </th>
                  <th className="text-left font-medium px-4 py-3">Status</th>
                  <th className="px-5 sm:px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {team.map((m) => (
                  <tr
                    key={m.id}
                    className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition"
                  >
                    <td className="px-5 sm:px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar name={m.name} size="md" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-zinc-950 dark:text-zinc-50 truncate">
                              {m.name}
                            </span>
                            {m.role === 'owner' && (
                              <span className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300">
                                Owner
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400 mono truncate">
                            {m.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-zinc-700 dark:text-zinc-200">{ROLE_LABEL[m.role] ?? m.role}</td>
                    <td className="px-4 py-4 text-zinc-600 dark:text-zinc-300 hidden md:table-cell tabular-nums whitespace-nowrap">
                      {m.joinedAt ? formatDate(m.joinedAt) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      {m.status === 'active' && <Badge tone="success">Active</Badge>}
                      {m.status === 'invited' && <Badge tone="warning">Invited</Badge>}
                      {m.status === 'disabled' && <Badge tone="error">Disabled</Badge>}
                    </td>
                    <td className="px-5 sm:px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        {m.status === 'invited' && (
                          <button
                            type="button"
                            className="px-2 py-1 rounded-md text-xs font-medium text-brand dark:text-brand-dm hover:bg-brand-subtle dark:hover:bg-brand-dm-subtle/30 transition inline-flex items-center gap-1"
                            title="Resend invite"
                          >
                            <Send className="w-3.5 h-3.5" />
                            Resend
                          </button>
                        )}
                        {m.role !== 'owner' && (
                          <button
                            type="button"
                            className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
                            title="More"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <p className="mt-6 text-sm text-zinc-500 dark:text-zinc-400">
        Cashiers and managers can sign in to the POS but never see Subscription, Billing, or
        Team — those routes are limited to Admin.
      </p>

      <InviteTeamModal open={inviteOpen} onClose={() => setInviteOpen(false)} businessId={business?.id ?? ''} />
    </div>
  );
}

function InviteTeamModal({ open, onClose, businessId }: { open: boolean; onClose: () => void; businessId: string }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'Cashier' | 'Manager' | 'Admin'>('Cashier');
  const [allLocations, setAllLocations] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!email.trim() || !businessId) {
      setError('A valid email is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiPost(`/businesses/${businessId}/team`, { email: email.trim(), name: name.trim() || undefined, role: role.toLowerCase() });
      void qc.invalidateQueries({ queryKey: ['team', businessId] });
      setEmail('');
      setName('');
      setRole('Cashier');
      onClose();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409 ? 'That person is already on the team.' : status === 403 ? 'Only an owner or admin can invite.' : 'Could not send the invite.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite team member"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy} iconLeft={<Send className="w-4 h-4" />}>
            {busy ? 'Sending…' : 'Send invite'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <Input
          label="Email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="cashier@example.com"
        />
        <Input
          label="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Rina Akter"
        />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            POS role <span className="text-rose-600">*</span>
          </span>
          <div className="grid grid-cols-3 gap-2">
            {(['Cashier', 'Manager', 'Admin'] as const).map((r) => (
              <OptionCard
                key={r}
                selected={role === r}
                onSelect={() => setRole(r)}
                className="text-center py-3"
              >
                <span className="text-sm font-medium text-zinc-950 dark:text-zinc-50">{r}</span>
              </OptionCard>
            ))}
          </div>
          <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
            {role === 'Cashier' && 'Can ring up sales only.'}
            {role === 'Manager' && 'Can ring up sales, manage staff, and read reports.'}
            {role === 'Admin' && 'Full access including billing and team management.'}
          </p>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            Locations
          </span>
          <div className="space-y-2">
            <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={allLocations}
                onChange={(e) => setAllLocations(e.target.checked)}
                className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
              />
              All locations
            </label>
            <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 px-3 py-2 rounded-md border border-zinc-200 dark:border-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                disabled={allLocations}
                className="rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
              />
              Khan Bakery — Main
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
}
